import React, { createContext, useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";

const HelpContext = createContext(null);

const HELP_REGISTRY = [
  {
    id: "costplanner-advanced",
    match: (pathname) => pathname.includes("/costplanner/advanced") || pathname.includes("/planner"),
    title: "Advanced Cost Planner",
    summary:
      "Use the Advanced Cost Planner to see true cost per piece, including materials, staff, kiln, packaging and overheads.",
    bullets: [
      "Fill in all materials and staff time to get an accurate cost per piece.",
      "Use the profit slider to see how different margins affect your final price.",
      "The Pricing & margin coach suggests wholesale and retail prices based on your target margins.",
    ],
  },
  {
    id: "kiln-planner",
    match: (pathname) => pathname.includes("/kiln-planner"),
    title: "Kiln Planner",
    summary: "Kiln Planner helps you plan firings, check capacity and avoid half-empty or overloaded kiln loads.",
    bullets: [
      "Choose a kiln on the left and add batches on the right to see how full the firing is.",
      "Mudo estimates usable space units based on your kiln size and product dimensions.",
      "Future updates will suggest how to split or top up firings automatically.",
    ],
  },
  {
    id: "kilns-settings",
    match: (pathname) => pathname.includes("/kilns"),
    title: "Kiln settings",
    summary: "Add your kilns with internal dimensions so Mudo can estimate capacity and help plan firings.",
    bullets: [
      "Enter internal width, depth and height to auto-calculate an approximate litre volume.",
      "You can override usable space units if you know how many mugs/plates fit in a typical firing.",
      "These values feed into the Kiln Planner and cost calculations.",
    ],
  },
];

function findHelpForPath(pathname) {
  for (const entry of HELP_REGISTRY) {
    if (entry.match(pathname)) return entry;
  }
  return null;
}

export function HelpProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const currentHelp = useMemo(() => findHelpForPath(location.pathname), [location.pathname]);

  const openHelp = () => setIsOpen(true);
  const closeHelp = () => setIsOpen(false);
  const toggleHelp = () => setIsOpen((prev) => !prev);

  async function askAi(questionText) {
    const q = (questionText || aiQuestion || "").trim();
    if (!q) return;

    setAiLoading(true);
    setAiError(null);

    try {
      const payload = {
        question: q,
        context: {
          screen: currentHelp?.title || location.pathname,
          extra: {
            pathname: location.pathname,
          },
        },
      };

      const res = await apiFetch("/api/ai/help", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!json.ok) {
        throw new Error(json.error || "AI error");
      }

      setAiQuestion(q);
      setAiAnswer(json.answer);
    } catch (err) {
      console.error("[HelpContext] askAi error", err);
      setAiError("Mudo AI couldn’t answer right now. Try again in a moment.");
    } finally {
      setAiLoading(false);
    }
  }

  const value = {
    isOpen,
    openHelp,
    closeHelp,
    toggleHelp,
    currentHelp,
    aiAnswer,
    aiQuestion,
    aiLoading,
    aiError,
    setAiQuestion,
    askAi,
  };

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp() {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error("useHelp must be used within HelpProvider");
  return ctx;
}
